import { analytics } from '@/common/analytics/analytics'
import Money from '@/common/models/money.ts'
import { Button } from '@/common/ui'
import { useState } from 'react'
import AddFundsModal from '@/wallet/AddFundsModal'
import useKycStore from '@/kyc/kycStore'
import { useFetchBalance, useFetchPayId } from '@/wallet/walletQueries'
import { Wallet } from 'lucide-react'
import useAccountStore from '@/account/accountStore'
import { ANALYTICS_EVENTS } from '@/common/analytics/events'

function WalletSummaryView({
    availableBalance,
    isLoading = false,
    disableAddFunds = true,
    onAdd,
}: {
    availableBalance?: number
    isLoading?: boolean
    disableAddFunds?: boolean
    onAdd?: () => void
}) {
    return (
        <div className="gap-2 flex flex-col">
            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider"></div>
            <div className="self-stretch inline-flex justify-between items-center">
                <div className="flex justify-start items-center gap-2">
                    <div className="flex justify-start items-end gap-2">
                        <Wallet className="text-content-secondary" />
                        <div className="justify-start text-content-secondary text-sm font-normal font-['Inter'] leading-5">
                            Available balance:
                        </div>
                        <div className="justify-start text-black text-sm font-medium font-['Inter'] leading-5">
                            {isLoading ? (
                                <div className="h-4 w-28 bg-stone-100 rounded" />
                            ) : (
                                `${Money.of(availableBalance)}`
                            )}
                        </div>
                    </div>
                </div>

                <Button
                    variant="outline"
                    disabled={disableAddFunds}
                    className="h-10 px-6 py-2 bg-primary-dust rounded-[100px] flex justify-center items-center gap-2.5"
                    onClick={() => {
                        analytics.trackWithDefaults(ANALYTICS_EVENTS.WALLET.ADD_FUNDS_BUTTON_CLICKED, {
                            source_page: 'dashboard',
                        })
                        if (onAdd) onAdd()
                    }}
                >
                    <div className="justify-start text-content-primary text-sm font-medium font-['Inter'] leading-5">
                        Add
                    </div>
                </Button>
            </div>
            <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider"></div>
        </div>
    )
}

export function WalletSummary() {
    const activeAccountId = useAccountStore((s) => s.activeAccountId)
    const { kycComplete } = useKycStore()
    const { data: balanceData, isLoading } = useFetchBalance(activeAccountId)
    const { data: payIdDetails } = useFetchPayId(activeAccountId, kycComplete)
    const [isFundDetailsOpen, setIsFundDetailsOpen] = useState(false)

    const openFundDetails = () => {
        setIsFundDetailsOpen(true)
        analytics.trackWithDefaults(ANALYTICS_EVENTS.WALLET.ADD_FUNDS_BUTTON_CLICKED, {
            source_page: 'dashboard',
        })
    }

    const available = balanceData?.availableBalance ?? 0

    return (
        <>
            <WalletSummaryView
                availableBalance={available}
                isLoading={isLoading}
                disableAddFunds={!payIdDetails}
                onAdd={() => openFundDetails()}
            />

            {payIdDetails && (
                <AddFundsModal
                    open={isFundDetailsOpen}
                    onClose={() => setIsFundDetailsOpen(false)}
                    payIdDetails={payIdDetails}
                />
            )}
        </>
    )
}

export default WalletSummary
